"use client";

/**
 * A residence as the residences hub draws it (moved out of
 * app/[locale]/residences/ResidencesIndexClient so the developers' offers
 * page uses the same card, owner 2026-10-21: "use whatever we already
 * have"). Phones: a row (small art on the left, the facts beside it), so
 * hundreds of residences are a list and not a wall of cards
 * (owner, 2026-10-09); from sm up: the card. The caller says what the
 * two fact slots hold: the price level on the left, the count or the
 * activity on the right.
 */
import Link from "@/lib/localized-link";
import ResidenceImage from "@/components/residences/ResidenceImage";

export default function ResidenceCard({
  id, name, district, image, imageFallback, value, meta, rank,
}: {
  id: string;
  name: string;
  district?: string | null;
  /** The photo (hosted hero or a listing site's photo); a placeholder scene without one. */
  image?: string | null;
  /** Tried when `image` fails, before the placeholder scene. */
  imageFallback?: string | null;
  /** The left fact: the AZN/m² level, or what stands for it. */
  value: React.ReactNode;
  /** The right fact: counts or activity. */
  meta?: React.ReactNode;
  /** The ranking page's place number. */
  rank?: number;
}) {
  return (
    <Link
      href={`/residence/${encodeURIComponent(id)}`}
      className="group flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white p-2 transition-all hover:border-gray-300 sm:block sm:overflow-hidden sm:rounded-2xl sm:p-0"
    >
      <div className="relative w-20 shrink-0 overflow-hidden rounded-lg sm:w-full sm:rounded-none">
        <ResidenceImage src={image} fallbackSrc={imageFallback} alt={name} className="h-14 w-20 sm:h-28 sm:w-full" />
        {rank != null && (
          <span className="absolute left-1 top-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-white sm:left-2 sm:top-2 sm:px-2 sm:text-[11px]">#{rank}</span>
        )}
      </div>
      <div className="min-w-0 flex-1 sm:p-3">
        <h3 title={name} className="truncate text-sm font-semibold text-gray-900 underline-offset-2 group-hover:underline">
          {name}
        </h3>
        {district && <div className="mt-0.5 truncate text-[11px] text-gray-400">{district}</div>}
        <div className="mt-1.5 flex items-baseline justify-between gap-2">
          {value}
          {meta != null && <span className="shrink-0 text-[11px] text-gray-400 tabular-nums">{meta}</span>}
        </div>
      </div>
    </Link>
  );
}
